import React from 'react'

import { observer } from 'mobx-react-lite'

import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'

import { Text } from '@mxenabled/mxui'

import {
  Dialog,
  Drawer,
  useBudgetsStore,
  useGlobalCopyStore,
  useGlobalUiStore,
  useTransactionStore,
} from '../../common'
import { type Budget, type DetailedBudget, type Transaction } from '../../core'

import { BudgetDetails } from './budgetdetails/BudgetDetails'
import { BudgetAmount } from './shared/BudgetAmount'
import { buildDynamicCopy } from '../../core/utils/Localization'
import { isInvalidAmount } from '../../core/utils/BudgetUtil'
import { SubBudgetAmounts } from './budgetdetails/SubBudgetAmounts'

const seedSubAmounts = (b?: DetailedBudget) =>
  Object.fromEntries((b?.subBudgets ?? []).map((sb) => [sb.guid, `${sb.amount}`]))

export const BudgetDetailsDrawer = observer(() => {
  const { selectedAccountGuids } = useGlobalUiStore()
  const { budgets: copy, common: commonCopy } = useGlobalCopyStore()
  const {
    alert,
    dateRange,
    deleteBudget,
    selectedBudget,
    selectedSubBudget,
    setAlert,
    setSelectedBudget,
    updateBudget,
  } = useBudgetsStore()
  const { setFilter } = useTransactionStore()

  const [isOpen, setIsOpen] = React.useState(false)
  const [isEditOpen, setIsEditOpen] = React.useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false)
  const [amount, setAmount] = React.useState(``)
  const [subBudgetAmounts, setSubBudgetAmounts] = React.useState<Record<string, string>>({})

  React.useEffect(() => {
    if (selectedBudget && !selectedSubBudget) {
      setAmount(`${selectedBudget.amount}`)
      setSubBudgetAmounts(seedSubAmounts(selectedBudget))
      setIsOpen(true)
      setFilter({
        accounts: selectedAccountGuids,
        dateRange: dateRange,
        custom: (t: Transaction) =>
          t.category_guid === selectedBudget.category_guid ||
          t.top_level_category_guid === selectedBudget.category_guid,
        showSplits: true,
      })
    }
  }, [selectedBudget])

  const handleUpdateBudget = async () => {
    if (selectedBudget) {
      let allSucceeded = true

      if (selectedBudget.subBudgets.length > 0) {
        const results = await Promise.all(
          selectedBudget.subBudgets.map((subBudget) => {
            const newAmount = Number(subBudgetAmounts[subBudget.guid] ?? subBudget.amount)
            return updateBudget({
              ...subBudget,
              amount: newAmount,
              percentage: newAmount ? (subBudget.category.currentAmount / newAmount) * 100 : 0,
            } as Budget)
          }),
        )
        allSucceeded = results.every(Boolean)
      } else {
        const result = await updateBudget({
          ...selectedBudget,
          amount: Number(amount),
          percentage: Number(amount)
            ? (selectedBudget.category.totalAmount / Number(amount)) * 100
            : 0,
        } as Budget)
        allSucceeded = result
      }

      setAlert(
        allSucceeded
          ? {
              msg: buildDynamicCopy(
                copy.alert_budget_updated,
                selectedBudget.category.name,
              ) as string,
              severity: 'success',
            }
          : {
              msg: buildDynamicCopy(
                copy.error_alert_edit_merchant_budget,
                selectedBudget.category.name,
              ) as string,
              severity: 'error',
            },
      )
      setIsEditOpen(false)
    }
  }

  const handleDeleteBudget = async () => {
    if (selectedBudget) {
      const succeeded = await deleteBudget(selectedBudget.guid)
      setIsDeleteOpen(false)
      setAlert(
        succeeded
          ? {
              msg: buildDynamicCopy(
                copy.alert_budget_deleted,
                selectedBudget.category.name,
              ) as string,
              severity: 'success',
            }
          : {
              msg: buildDynamicCopy(
                copy.error_alert_delete_merchant_budget,
                selectedBudget.category.name,
              ) as string,
              severity: 'error',
            },
      )
      if (succeeded) {
        handleClose()
      }
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    // Wait for the close animation before set the selected budget
    setTimeout(() => {
      setSelectedBudget(undefined)
    }, 250)
  }

  const handleEditClose = () => {
    setIsEditOpen(false)
    setAmount(`${selectedBudget?.amount}`)
    setSubBudgetAmounts(seedSubAmounts(selectedBudget))
  }

  if (!selectedBudget) {
    return undefined
  }

  const hasSubBudgets = selectedBudget.subBudgets.length > 0
  const disablePrimaryButton = hasSubBudgets
    ? selectedBudget.subBudgets.some((sb) =>
        isInvalidAmount(subBudgetAmounts[sb.guid] ?? `${sb.amount}`),
      )
    : isInvalidAmount(amount)

  return (
    <React.Fragment>
      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={isOpen}
        onClose={handleClose}
        onPrimaryAction={() => setIsEditOpen(true)}
        onSecondaryAction={() => setIsDeleteOpen(true)}
        primaryText={copy.details_edit_button}
        secondaryColor="error"
        secondaryText={copy.details_delete_button}
        title={copy.details_title}
      >
        <BudgetDetails budget={selectedBudget} />
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          autoHideDuration={3500}
          onClose={() => setAlert(null)}
          open={Boolean(alert)}
        >
          <Alert
            closeText=""
            onClose={() => setAlert(null)}
            role={alert?.severity === 'error' ? 'alert' : undefined}
            severity={alert?.severity ?? 'success'}
            variant="filled"
          >
            {alert?.msg}
          </Alert>
        </Snackbar>
      </Drawer>

      <Dialog
        copy={{ close_aria: commonCopy.close_aria, title: copy.details_edit_title }}
        disablePrimaryButton={disablePrimaryButton}
        isOpen={isEditOpen}
        onClose={handleEditClose}
        onPrimaryAction={handleUpdateBudget}
        onSecondaryAction={handleEditClose}
        primaryText={copy.edit_save_button}
        sx={{
          '& .MuiStack-root:first-of-type': {
            pb: 8,
          },
        }}
      >
        {!hasSubBudgets && (
          <BudgetAmount
            amount={amount}
            categoryName={selectedBudget.category.name}
            onAmountChanged={setAmount}
          />
        )}
        {hasSubBudgets && (
          <SubBudgetAmounts
            categoryName={selectedBudget.category.name}
            onSubBudgetAmountChanged={(guid, v) =>
              setSubBudgetAmounts((p) => ({ ...p, [guid]: v }))
            }
            subBudgetAmounts={subBudgetAmounts}
            subBudgets={selectedBudget.subBudgets}
          />
        )}
      </Dialog>

      <Dialog
        copy={{ close_aria: commonCopy.close_aria, title: copy.delete_title }}
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onPrimaryAction={handleDeleteBudget}
        primaryColor="error"
        primaryText={copy.details_delete_button}
        secondaryColor="secondary"
      >
        <Text variant="subtitle1">
          {buildDynamicCopy(copy.delete_description, selectedBudget.category.name) as string}
        </Text>
      </Dialog>
    </React.Fragment>
  )
})
